import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, LogOut, Send, CheckCircle, Handshake, IndianRupee, MessageSquare, ArrowLeft, Check, CheckCheck, Paperclip, Image, FileText, Video, X, Download, Trash2, CreditCard, Clock, AlertCircle, ThumbsUp, Loader2, BadgeCheck, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import StarAvatarFallback from "@/components/StarAvatarFallback";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import BottomNav from "@/components/BottomNav";
import ChatPanel from "@/components/chat/ChatPanel";
import ShareDealDialog from "@/components/ShareDealDialog";
import { useAuth, API } from "@/App";

const BrandHub = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const [profile, setProfile] = useState(null);
  const [requests, setRequests] = useState([]);
  const [deals, setDeals] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  // New unified chat state: { threadId?: str, refId?: str } or null
  const [chatView, setChatView] = useState(null);
  const [deletingRequest, setDeletingRequest] = useState(null);
  const [deletingDeal, setDeletingDeal] = useState(null);
  const [deletingConversation, setDeletingConversation] = useState(null);
  const [processingDeal, setProcessingDeal] = useState(null);
  const [counterOfferAmount, setCounterOfferAmount] = useState({});
  const [showCounterOffer, setShowCounterOffer] = useState({});
  const [selectedDealForRating, setSelectedDealForRating] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingValue, setRatingValue] = useState(5);
  const [ratingReview, setRatingReview] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedItemForPayment, setSelectedItemForPayment] = useState(null);
  const [selectedDeadline, setSelectedDeadline] = useState(14);
  
  // Share Deal Dialog
  const [shareDealDialogOpen, setShareDealDialogOpen] = useState(false);
  const [shareDealData, setShareDealData] = useState(null);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
    fetchRequests();
    fetchDeals();
    fetchConversations();
    
    // Auto-refresh for real-time updates (every 10 seconds)
    const pollInterval = setInterval(() => {
      fetchRequests();
      fetchDeals();
      fetchConversations();
    }, 10000);
    
    return () => clearInterval(pollInterval);
  }, []);

  // Real-time message polling is handled internally by ChatPanel

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/brand/profile`, { 
        
        headers: getAuthHeaders()
      });
      setProfile(response.data);
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      // Fetch both old collaboration requests AND new talent bookings
      const [collabRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/brand/requests`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/talent/bookings/sent`, { headers: getAuthHeaders() }).catch(() => ({ data: { bookings: [] } }))
      ]);
      
      const oldRequests = collabRes.data || [];
      const newBookings = (bookingsRes.data.bookings || []).map(booking => ({
        request_id: booking.booking_id,
        influencer_id: booking.talent_id,
        influencer_name: booking.talent_name,
        message: booking.project_description,
        deal_amount: booking.budget,
        current_offer: booking.counter_offer_amount || booking.budget,
        status: booking.status,
        created_at: booking.created_at,
        is_new_system: true,
        negotiation_status: booking.status === 'negotiating' ? 'negotiating' : null,
        last_offer_by: booking.counter_offer_by
      }));
      
      setRequests([...oldRequests, ...newBookings]);
    } catch (error) {
      // Ignore
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/chat/threads`, { 
        headers: getAuthHeaders()
      });
      setConversations(response.data.threads || []);
    } catch (error) {
      // Ignore
    }
  };

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API}/deals`, { 
        
        headers: getAuthHeaders()
      });
      setDeals(response.data);
    } catch (error) {
      // Ignore
    }
  };

  const handlePayForDeal = async (item, deadlineDays = 14) => {
    // item can be either a deal or a collaboration request with agreed negotiation
    const dealId = item.deal_id;
    const requestId = item.request_id;
    const isNewSystem = item.is_new_system;
    
    setProcessingDeal(dealId || requestId);
    setShowPaymentModal(false);
    try {
      let orderRes;
      
      if (dealId) {
        // Pay for existing deal - update deadline first
        await axios.put(`${API}/deals/${dealId}/set-deadline`, 
          { deadline_days: deadlineDays },
          { headers: getAuthHeaders() }
        ).catch(() => {}); // Ignore if endpoint doesn't exist yet
        
        orderRes = await axios.post(`${API}/deals/create-order`, 
          { deal_id: dealId },
          { headers: getAuthHeaders() }
        );
      } else if (requestId && isNewSystem) {
        // New talent booking system - pay directly
        orderRes = await axios.post(`${API}/talent/booking/${requestId}/pay`, 
          {},
          { headers: getAuthHeaders() }
        );
      } else if (requestId) {
        // Old system - Create deal from request and pay with deadline
        const acceptRes = await axios.post(`${API}/brand/request/${requestId}/accept-and-pay`, 
          { amount: item.current_offer || item.deal_amount, deadline_days: deadlineDays },
          { headers: getAuthHeaders() }
        );
        orderRes = acceptRes;
      }
      
      const options = {
        key: orderRes.data.key_id,
        amount: orderRes.data.amount,
        currency: orderRes.data.currency,
        name: "FOUNDSTAR",
        description: `Deal payment to ${item.influencer_name || item.talent_name || 'Talent'}`,
        order_id: orderRes.data.order_id,
        handler: async (response) => {
          try {
            if (isNewSystem) {
              // New system payment verification
              await axios.post(`${API}/talent/booking/${requestId}/verify-payment`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              }, { headers: getAuthHeaders() });
            } else {
              await axios.post(`${API}/deals/verify-payment`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              }, { headers: getAuthHeaders() });
            }
            
            toast.success("Payment successful! Deal is now in progress.");
            fetchRequests();
            fetchDeals();
          } catch (error) {
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          email: user?.email || "",
          name: user?.name || "",
          contact: ""
        },
        theme: {
          color: "#FF69B4"
        }
      };
      
      const razorpay = new window.Razorpay(options);
      
      // Android fix: Small delay before opening to ensure touch events register
      setTimeout(() => {
        razorpay.open();
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setProcessingDeal(null);
    }
  };

  const handleStartBarter = async (requestId) => {
    if (!confirm("Mark product as sent? This will start the barter collaboration.")) return;
    
    setProcessingDeal(requestId);
    try {
      const response = await axios.post(`${API}/brand/request/${requestId}/barter-start`, 
        { deadline_days: selectedDeadline },
        { headers: getAuthHeaders() }
      );
      toast.success("Barter collaboration started! Product marked as sent.");
      fetchRequests();
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start barter collaboration");
    } finally {
      setProcessingDeal(null);
    }
  };

  const handleApproveDeal = async (dealId) => {
    if (!confirm("Approve this deal and release payment to the influencer?")) return;
    
    setProcessingDeal(dealId);
    try {
      const response = await axios.post(`${API}/deals/${dealId}/approve`, {}, {
        
        headers: getAuthHeaders()
      });
      toast.success(`Payment of ₹${response.data.influencer_payout} released to influencer!`);
      fetchDeals();
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve deal");
    } finally {
      setProcessingDeal(null);
    }
  };

  const handleDisputeDeal = async (dealId) => {
    const reason = prompt("Please provide a reason for the dispute:");
    if (!reason) return;
    
    setProcessingDeal(dealId);
    try {
      await axios.post(`${API}/deals/${dealId}/dispute`, {}, {
        
        headers: getAuthHeaders(),
        params: { reason }
      });
      toast.success("Dispute raised. Admin will review and resolve.");
      fetchDeals();
    } catch (error) {
      toast.error("Failed to raise dispute");
    } finally {
      setProcessingDeal(null);
    }
  };

  // Helper to construct proper file URL (kept for avatar usage elsewhere)
  const getFileUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/files')) return `${API}${url.replace('/api', '')}`;
    return `${API}/files/${url}`;
  };
  // Silence unused-var linter for helper
  // eslint-disable-next-line no-unused-vars
  const _getFileUrl = getFileUrl;

  const openPaymentModal = (item) => {
    setSelectedItemForPayment(item);
    setSelectedDeadline(14);
    setShowPaymentModal(true);
  };

  const confirmPayment = () => {
    if (selectedItemForPayment) {
      handlePayForDeal(selectedItemForPayment, selectedDeadline);
    }
  };

  const submitRating = async () => {
    if (!selectedDealForRating) return;
    try {
      await axios.post(`${API}/deals/${selectedDealForRating.deal_id}/rate`, {
        rating: ratingValue,
        review: ratingReview || null
      }, { headers: getAuthHeaders() });
      
      toast.success("Rating submitted! Thank you for your feedback.");
      setShowRatingModal(false);
      setSelectedDealForRating(null);
      setRatingValue(5);
      setRatingReview("");
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit rating");
    }
  };

  const getDealStatusBadge = (status) => {
    const statusConfig = {
      pending_payment: { color: "bg-yellow-100 text-yellow-800", label: "Pending Payment" },
      in_progress: { color: "bg-blue-100 text-blue-800", label: "In Progress" },
      pending_approval: { color: "bg-purple-100 text-purple-800", label: "Pending Approval" },
      completed: { color: "bg-green-100 text-green-800", label: "Completed" },
      disputed: { color: "bg-red-100 text-red-800", label: "Disputed" },
      refunded: { color: "bg-gray-100 text-gray-800", label: "Refunded" }
    };
    const config = statusConfig[status] || { color: "bg-gray-100 text-gray-800", label: status };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const handleCounterOffer = async (requestId) => {
    const amount = counterOfferAmount[requestId];
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    try {
      await axios.post(`${API}/collaboration/${requestId}/counter-offer`, 
        { amount: parseInt(amount) }, 
        { headers: getAuthHeaders() }
      );
      toast.success("Counter offer sent!");
      setShowCounterOffer({ ...showCounterOffer, [requestId]: false });
      setCounterOfferAmount({ ...counterOfferAmount, [requestId]: "" });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send counter offer");
    }
  };

  const handleAcceptOffer = async (requestId) => {
    try {
      await axios.post(`${API}/collaboration/${requestId}/accept-offer`, {}, { 
        headers: getAuthHeaders() 
      });
      toast.success("Offer accepted! You can now proceed with payment.");
      fetchRequests();
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept offer");
    }
  };

  // New unified chat: open by thread or ref (request/booking id)
  // eslint-disable-next-line no-unused-vars
  const openChatByThread = (threadId) => {
    setChatView({ threadId });
  };
  const openChatByRef = (refId) => {
    setChatView({ refId });
  };
  const closeChat = () => {
    setChatView(null);
    fetchConversations();
  };

  // Delete handlers for requests, deals, and conversations
  const handleDeleteRequest = async (requestId) => {
    if (!confirm("Delete this collaboration request?")) return;
    
    setDeletingRequest(requestId);
    try {
      await axios.delete(`${API}/brand/request/${requestId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Request deleted");
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete request");
    } finally {
      setDeletingRequest(null);
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (!confirm("Delete this deal from your history?")) return;
    
    setDeletingDeal(dealId);
    try {
      await axios.delete(`${API}/brand/deal/${dealId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Deal deleted");
      fetchDeals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete deal");
    } finally {
      setDeletingDeal(null);
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    if (!confirm("Delete this conversation? All messages will be lost.")) return;
    
    setDeletingConversation(conversationId);
    try {
      await axios.delete(`${API}/conversation/${conversationId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Conversation deleted");
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete conversation");
    } finally {
      setDeletingConversation(null);
    }
  };
  // eslint-disable-next-line no-unused-vars
  const _handleDeleteConversation = handleDeleteConversation;

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF69B4] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show conversation view if active
  if (chatView) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        {/* Conversation Header */}
        <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={closeChat}
              className="text-[#64748B]"
              data-testid="chat-back-page-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="font-outfit font-semibold text-[#1E293B]">Chat</h2>
              <p className="text-xs text-[#64748B]">Collaboration Chat</p>
            </div>
          </div>
        </header>

        <div className="max-w-6xl mx-auto w-full p-4 flex-1">
          <ChatPanel
            accentColor="#87CEEB"
            initialThreadId={chatView.threadId || null}
            initialRefId={chatView.refId || null}
            onBack={closeChat}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-nav">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#87CEEB]/20 flex items-center justify-center">
              <Star className="w-4 h-4 text-[#FF69B4]" />
            </div>
            <span className="font-outfit font-bold text-lg text-[#1E293B]">Brand Hub</span>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleLogout}
            className="text-[#64748B] hover:text-[#FF69B4]"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="stats-card rounded-2xl p-4" data-testid="stat-sent">
            <Send className="w-8 h-8 text-[#FF69B4] mb-2" />
            <p className="font-outfit font-bold text-2xl text-[#1E293B]">{profile?.requests_sent || 0}</p>
            <p className="text-sm text-[#64748B]">Requests Sent</p>
          </div>
          <div className="stats-card rounded-2xl p-4" data-testid="stat-approved">
            <CheckCircle className="w-8 h-8 text-[#87CEEB] mb-2" />
            <p className="font-outfit font-bold text-2xl text-[#1E293B]">{profile?.approved_requests || 0}</p>
            <p className="text-sm text-[#64748B]">Approved</p>
          </div>
          <div className="stats-card rounded-2xl p-4" data-testid="stat-closed">
            <Handshake className="w-8 h-8 text-[#FF69B4] mb-2" />
            <p className="font-outfit font-bold text-2xl text-[#1E293B]">{profile?.closed_deals || 0}</p>
            <p className="text-sm text-[#64748B]">Closed Deals</p>
          </div>
          <div className="stats-card rounded-2xl p-4" data-testid="stat-spent">
            <IndianRupee className="w-8 h-8 text-[#87CEEB] mb-2" />
            <p className="font-outfit font-bold text-2xl text-[#1E293B]">{formatCurrency(profile?.money_spent || 0)}</p>
            <p className="text-sm text-[#64748B]">Money Spent</p>
          </div>
        </div>

        {/* Tabs for Requests, Deals, Messages */}
        <Tabs defaultValue="deals" className="mb-6">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="requests">Requests</TabsTrigger>
            <TabsTrigger value="deals">Deals</TabsTrigger>
            <TabsTrigger value="messages">Messages</TabsTrigger>
          </TabsList>

          {/* Collaboration Requests Tab */}
          <TabsContent value="requests">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="font-outfit font-semibold text-lg text-[#1E293B] mb-4">Collaboration Requests</h2>
              
              {requests.length === 0 ? (
                <div className="text-center py-8">
                  <Send className="w-12 h-12 text-[#E2E8F0] mx-auto mb-3" />
                  <p className="text-[#64748B]">No requests sent yet</p>
                  <p className="text-sm text-[#94A3B8]">Go to Discover to find influencers</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {requests.map((req) => (
                    <div key={req.request_id} className="p-4 bg-[#F8FAFC] rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-[#1E293B]">To: {req.influencer_name || 'Influencer'}</span>
                          {req.influencer_verified && <BadgeCheck className="w-4 h-4 text-[#87CEEB]" />}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            req.status === 'negotiating' ? 'bg-blue-100 text-blue-700' :
                            req.status === 'accepted' ? 'bg-green-100 text-green-700' :
                            req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            req.status === 'expired' ? 'bg-gray-100 text-gray-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {req.status === 'negotiating' ? 'Negotiating' : req.status}
                          </span>
                          {/* Message button for any active request */}
                          {['pending', 'negotiating', 'accepted', 'paid', 'in_progress', 'completed'].includes(req.status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openChatByRef(req.request_id)}
                              className="h-7 w-7 text-[#87CEEB] hover:bg-sky-50"
                              data-testid={`message-request-${req.request_id}`}
                              title="Message"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Delete button for pending/rejected/expired requests */}
                          {['pending', 'rejected', 'expired'].includes(req.status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteRequest(req.request_id)}
                              disabled={deletingRequest === req.request_id}
                              className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                              data-testid={`delete-request-${req.request_id}`}
                            >
                              {deletingRequest === req.request_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-[#64748B]">{req.message || "Collaboration request sent"}</p>
                      
                      {/* Negotiation Status Display */}
                      {req.deal_amount && (
                        <div className="bg-white p-3 rounded-lg border border-[#E2E8F0] my-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs text-[#64748B]">
                              {req.negotiation_status === 'agreed' ? 'Agreed Amount' : 
                               req.negotiation_status === 'negotiating' ? 'Current Offer' : 'Your Offer'}
                            </span>
                            {req.last_offer_by && (
                              <span className="text-xs text-[#94A3B8]">
                                by {req.last_offer_by === 'brand' ? 'You' : 'Influencer'}
                              </span>
                            )}
                          </div>
                          <p className="text-lg font-bold text-[#FF69B4]">
                            ₹{(req.current_offer || req.deal_amount).toLocaleString()}
                          </p>
                          {req.negotiation_status === 'agreed' && (
                            <p className="text-xs text-green-600 mt-1">
                              <CheckCircle className="w-3 h-3 inline mr-1" />
                              Amount agreed! Ready to proceed.
                            </p>
                          )}
                          {req.last_offer_at && req.status === 'negotiating' && (
                            <p className="text-xs text-[#94A3B8] mt-1">
                              <Clock className="w-3 h-3 inline mr-1" />
                              48hr to respond
                            </p>
                          )}
                        </div>
                      )}

                      {/* Counter Offer Input for Brand */}
                      {req.deal_amount && req.status === 'negotiating' && req.last_offer_by === 'influencer' && showCounterOffer[req.request_id] && (
                        <div className="flex gap-2 mb-3">
                          <Input
                            type="number"
                            placeholder="Your counter amount"
                            value={counterOfferAmount[req.request_id] || ""}
                            onChange={(e) => setCounterOfferAmount({
                              ...counterOfferAmount, 
                              [req.request_id]: e.target.value
                            })}
                            className="rounded-lg text-sm"
                          />
                          <Button
                            size="sm"
                            onClick={() => handleCounterOffer(req.request_id)}
                            className="rounded-full bg-blue-500 hover:bg-blue-600 whitespace-nowrap"
                          >
                            Send
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowCounterOffer({...showCounterOffer, [req.request_id]: false})}
                            className="rounded-full"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      {/* Action buttons for negotiating requests */}
                      {req.status === 'negotiating' && req.last_offer_by === 'influencer' && req.negotiation_status !== 'agreed' && !showCounterOffer[req.request_id] && (
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowCounterOffer({...showCounterOffer, [req.request_id]: true})}
                            className="rounded-full border-blue-200 text-blue-500 hover:bg-blue-50"
                          >
                            <IndianRupee className="w-4 h-4 mr-1" />
                            Counter
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleAcceptOffer(req.request_id)}
                            className="rounded-full bg-[#FF69B4] hover:bg-[#E05297]"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Accept ₹{(req.current_offer || req.deal_amount).toLocaleString()}
                          </Button>
                        </div>
                      )}

                      {/* Pay Now or Barter Start button when negotiation is agreed */}
                      {req.negotiation_status === 'agreed' && req.status !== 'accepted' && (
                        <div className="mt-3">
                          {req.is_barter ? (
                            <Button
                              size="sm"
                              onClick={() => handleStartBarter(req.request_id)}
                              disabled={processingDeal === req.request_id}
                              className="rounded-full bg-purple-500 hover:bg-purple-600 w-full"
                            >
                              {processingDeal === req.request_id ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4 mr-1" />
                              )}
                              Mark Product Sent (₹{(req.current_offer || req.deal_amount)?.toLocaleString()} value)
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => openPaymentModal(req)}
                              className="rounded-full bg-green-500 hover:bg-green-600 w-full"
                            >
                              <CreditCard className="w-4 h-4 mr-1" />
                              Pay ₹{(req.current_offer || req.deal_amount).toLocaleString()} & Start Deal
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Show Pay button when request is accepted (deal created but not paid) */}
                      {req.status === 'accepted' && (req.deal_amount || req.is_new_system) && (
                        <div className="mt-3">
                          {req.is_barter ? (
                            // Barter collaboration - no payment needed
                            <Button
                              size="sm"
                              onClick={() => handleStartBarter(req.request_id)}
                              disabled={processingDeal === req.request_id}
                              className="rounded-full bg-purple-500 hover:bg-purple-600 w-full"
                            >
                              {processingDeal === req.request_id ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4 mr-1" />
                              )}
                              Mark Product Sent (₹{(req.current_offer || req.deal_amount)?.toLocaleString()} value)
                            </Button>
                          ) : req.is_new_system ? (
                            // New talent booking system - direct pay
                            <Button
                              size="sm"
                              onClick={() => openPaymentModal(req)}
                              className="rounded-full bg-green-500 hover:bg-green-600 w-full"
                            >
                              <CreditCard className="w-4 h-4 mr-1" />
                              Pay ₹{(req.current_offer || req.deal_amount)?.toLocaleString()} Now
                            </Button>
                          ) : (
                            // Old system - check for deal
                            (() => {
                              const deal = deals.find(d => d.request_id === req.request_id);
                              if (deal && deal.status === 'pending_payment') {
                                return (
                                  <Button
                                    size="sm"
                                    onClick={() => openPaymentModal(deal)}
                                    className="rounded-full bg-green-500 hover:bg-green-600 w-full"
                                  >
                                    <CreditCard className="w-4 h-4 mr-1" />
                                    Pay ₹{deal.amount?.toLocaleString()} Now
                                  </Button>
                                );
                              } else if (deal && deal.status === 'in_progress') {
                                return (
                                  <div className="p-2 bg-blue-50 rounded-lg text-center">
                                    <Clock className="w-4 h-4 inline mr-2 text-blue-600" />
                                    <span className="text-sm text-blue-700">Deal in progress</span>
                                  </div>
                                );
                              } else if (deal && deal.status === 'completed') {
                                return (
                                  <div className="p-2 bg-green-50 rounded-lg text-center">
                                    <CheckCircle className="w-4 h-4 inline mr-2 text-green-600" />
                                    <span className="text-sm text-green-700">Deal completed!</span>
                                  </div>
                                );
                              }
                              return null;
                            })()
                          )}
                        </div>
                      )}

                      <p className="text-xs text-[#94A3B8] mt-2">
                        {new Date(req.created_at).toLocaleDateString('en-IN', { 
                          day: 'numeric', 
                          month: 'short', 
                          year: 'numeric' 
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Deals Tab */}
          <TabsContent value="deals">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="font-outfit font-semibold text-lg text-[#1E293B] mb-4">
                Active Deals
                {deals.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-[#64748B]">
                    {deals.length} deal{deals.length > 1 ? 's' : ''}
                  </span>
                )}
              </h2>
              
              {deals.length === 0 ? (
                <div className="text-center py-8">
                  <Handshake className="w-12 h-12 text-[#E2E8F0] mx-auto mb-3" />
                  <p className="text-[#64748B]">No active deals</p>
                  <p className="text-sm text-[#94A3B8]">When you send a request with deal amount, it will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {deals.map((deal) => (
                    <div key={deal.deal_id} className="p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="p-0 bg-transparent overflow-hidden">
                              <StarAvatarFallback iconClassName="w-5 h-5" />
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-[#1E293B]">{deal.influencer_name}</p>
                            <p className="text-lg font-bold text-[#FF69B4]">
                              {deal.is_barter ? (
                                <span className="flex items-center gap-1">
                                  🎁 ₹{deal.amount?.toLocaleString()} <span className="text-xs font-normal text-purple-600">(Barter)</span>
                                </span>
                              ) : (
                                `₹${deal.amount?.toLocaleString()}`
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {deal.is_barter && (
                            <Badge className="bg-purple-100 text-purple-700 text-xs">Barter</Badge>
                          )}
                          {getDealStatusBadge(deal.status)}
                          {/* Delete button for pending_payment, completed, or refunded deals */}
                          {['pending_payment', 'completed', 'refunded'].includes(deal.status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteDeal(deal.deal_id)}
                              disabled={deletingDeal === deal.deal_id}
                              className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                              data-testid={`delete-deal-${deal.deal_id}`}
                            >
                              {deletingDeal === deal.deal_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons based on status */}
                      <div className="flex gap-2 mt-3">
                        {deal.status === "pending_payment" && (
                          <Button
                            onClick={() => openPaymentModal(deal)}
                            disabled={processingDeal === deal.deal_id}
                            className="flex-1 bg-[#FF69B4] hover:bg-[#E05297] rounded-full"
                          >
                            {processingDeal === deal.deal_id ? "Processing..." : (
                              <>
                                <CreditCard className="w-4 h-4 mr-2" />
                                Pay Now
                              </>
                            )}
                          </Button>
                        )}
                        
                        {deal.status === "in_progress" && (
                          <div className="flex-1 text-center p-2 bg-blue-50 rounded-lg">
                            <Clock className="w-4 h-4 inline mr-2 text-blue-600" />
                            <span className="text-sm text-blue-700">Waiting for influencer to complete work</span>
                          </div>
                        )}
                        
                        {deal.status === "pending_approval" && (
                          <>
                            <Button
                              onClick={() => handleApproveDeal(deal.deal_id)}
                              disabled={processingDeal === deal.deal_id}
                              className="flex-1 bg-green-500 hover:bg-green-600 rounded-full"
                            >
                              {processingDeal === deal.deal_id ? "Processing..." : (
                                <>
                                  <ThumbsUp className="w-4 h-4 mr-2" />
                                  {deal.is_barter ? "Approve & Complete" : "Approve & Pay"}
                                </>
                              )}
                            </Button>
                            <Button
                              onClick={() => handleDisputeDeal(deal.deal_id)}
                              variant="outline"
                              className="rounded-full border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <AlertCircle className="w-4 h-4 mr-1" />
                              Dispute
                            </Button>
                          </>
                        )}
                        
                        {deal.status === "completed" && (
                          <div className="flex-1 space-y-2">
                            {deal.brand_rating ? (
                              <div className="text-center p-2 bg-green-50 rounded-lg">
                                <CheckCircle className="w-4 h-4 inline mr-2 text-green-600" />
                                <span className="text-sm text-green-700">
                                  Completed! Rated {deal.brand_rating}⭐
                                </span>
                              </div>
                            ) : (
                              <Button
                                onClick={() => {
                                  setSelectedDealForRating(deal);
                                  setShowRatingModal(true);
                                }}
                                className="w-full bg-yellow-500 hover:bg-yellow-600 rounded-full"
                              >
                                <Star className="w-4 h-4 mr-2" />
                                Rate Influencer
                              </Button>
                            )}
                            {/* Share Deal Button */}
                            <Button
                              onClick={() => {
                                setShareDealData({
                                  brand: profile,
                                  star: {
                                    user_id: deal.influencer_id,
                                    name: deal.influencer_name,
                                    picture: deal.influencer_photo,
                                    category: deal.influencer_category || "Creator",
                                    is_verified: deal.influencer_verified
                                  },
                                  dealAmount: deal.amount
                                });
                                setShareDealDialogOpen(true);
                              }}
                              variant="outline"
                              className="w-full rounded-full border-[#FF69B4] text-[#FF69B4] hover:bg-[#FF69B4] hover:text-white"
                              data-testid="share-deal-btn"
                            >
                              <Share2 className="w-4 h-4 mr-2" />
                              Share This Collab
                            </Button>
                          </div>
                        )}
                        
                        {deal.status === "disputed" && (
                          <div className="flex-1 text-center p-2 bg-red-50 rounded-lg">
                            <AlertCircle className="w-4 h-4 inline mr-2 text-red-600" />
                            <span className="text-sm text-red-700">Under admin review</span>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-xs text-[#94A3B8] mt-3">
                        Created: {new Date(deal.created_at).toLocaleDateString('en-IN', { 
                          day: 'numeric', month: 'short', year: 'numeric' 
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <ChatPanel 
              accentColor="#87CEEB"
              emptyTitle="No conversations yet"
              emptySubtitle="When stars accept your requests, chats will appear here."
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Rating Modal */}
      <Dialog open={showRatingModal} onOpenChange={setShowRatingModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Rate {selectedDealForRating?.influencer_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-center">
              <p className="text-sm text-[#64748B] mb-3">How was your experience?</p>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRatingValue(star)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star 
                      className={`w-10 h-10 ${star <= ratingValue ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
                    />
                  </button>
                ))}
              </div>
              <p className="text-lg font-medium mt-2">{ratingValue} / 5</p>
            </div>
            
            <div>
              <Label className="text-[#1E293B]">Review (optional)</Label>
              <Textarea
                placeholder="Share your experience working with this influencer..."
                value={ratingReview}
                onChange={(e) => setRatingReview(e.target.value)}
                className="mt-1 rounded-xl"
                rows={3}
              />
            </div>
            
            <Button
              onClick={submitRating}
              className="w-full bg-[#FF69B4] hover:bg-[#E05297] rounded-full"
            >
              Submit Rating
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Modal with Deadline Selection */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Confirm Payment</DialogTitle>
          </DialogHeader>
          {selectedItemForPayment && (
            <div className="space-y-4 py-4">
              <div className="text-center p-4 bg-[#FFF0F5] rounded-xl">
                <p className="text-sm text-[#64748B]">Amount to Pay</p>
                <p className="text-3xl font-bold text-[#FF69B4]">
                  ₹{(selectedItemForPayment.current_offer || selectedItemForPayment.deal_amount || selectedItemForPayment.amount)?.toLocaleString()}
                </p>
                <p className="text-sm text-[#64748B] mt-1">
                  to {selectedItemForPayment.influencer_name || 'Influencer'}
                </p>
              </div>
              
              <div>
                <Label className="text-[#1E293B] flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4" />
                  Work Completion Deadline
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  {[7, 14, 30].map((days) => (
                    <button
                      key={days}
                      onClick={() => setSelectedDeadline(days)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        selectedDeadline === days 
                          ? 'border-[#FF69B4] bg-[#FFF0F5]' 
                          : 'border-[#E2E8F0] hover:border-[#FF69B4]'
                      }`}
                    >
                      <p className="font-bold text-lg text-[#1E293B]">{days}</p>
                      <p className="text-xs text-[#64748B]">days</p>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#94A3B8] mt-2">
                  Influencer must complete work within this time. Late delivery may result in warnings.
                </p>
              </div>
              
              <div className="bg-[#FFF3CD] p-3 rounded-xl">
                <p className="text-sm text-[#856404]">
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  If work isn't completed within {selectedDeadline * 2} days, you can request a full refund.
                </p>
              </div>
              
              <Button
                onClick={confirmPayment}
                disabled={processingDeal}
                className="w-full bg-green-500 hover:bg-green-600 rounded-full"
              >
                {processingDeal ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Pay & Start Deal
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BottomNav userType="brand" />
      
      {/* Share Deal Dialog */}
      <ShareDealDialog
        open={shareDealDialogOpen}
        onClose={() => setShareDealDialogOpen(false)}
        dealData={shareDealData}
      />
    </div>
  );
};

export default BrandHub;
